// src/modules/units/tyres/views/retread-batch/retreadbatchpicker.js
//
// Orchestrator for the add-tyres picker. Lists the in-stock tyres (via the client
// sweep over effectiveStatus, the same rule the register uses — never a raw
// ?tyreStatus=), minus the ones already on this batch, and adds the selected ones
// through addRetreadBatchLines. That emits RETREAD_BATCH_CHANGED, so the detail
// behind refetches; this view just reports the result.
//
// Adds are all-or-nothing on the backend: if any tyre is ineligible the whole
// request is rejected and the offending tyres come back in tyreErrors[]. We mark
// those rows so the user can deselect them and retry. A successful add may carry a
// soft RETREAD_CAP_REACHED warning, surfaced as an amber note rather than a block.

import { fetchAllTyres } from '../tyres.api.js';
import { addRetreadBatchLines, fetchRetreadBatches, fetchRetreadBatch } from '../retread-batch.api.js';
import { getSelectedYardId } from '../../shared/tyre-scope.js';
import { effectiveStatus, TYRE_STATUS } from '../../shared/tyre-status.js';
import { RETREAD_WARNING, RETREAD_BATCH_STATUS } from '../../shared/retread-batch-core.js';
import { tyreErrorText } from '../../shared/retread-batch-vocab.js';
import { pickerHTML, pickerRowHTML, pickerStateRowHTML } from './retreadbatchpicker.view.js';

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

const idStr = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * @param {HTMLElement} viewEl controller view slot (emptied first)
 * @param {object} ctx
 * @param {object} ctx.batch the fresh batch (needs retreadBatchId; lines used to exclude)
 * @param {() => void} [ctx.onClose] cancel / done -> back to the detail
 * @returns {() => void} teardown
 */
export function renderRetreadBatchPicker(viewEl, ctx = {}) {
  let disposed = false;
  const batch = ctx.batch || {};
  const batchId = batch.retreadBatchId;
  const already = new Set((batch.lines || []).map((l) => idStr(l?.externalTyreId ?? l?.tyreId)).filter(Boolean));

  let candidates = [];          // in-stock, not already on this batch
  const selected = new Set();   // externalTyreId (string)
  const errors = new Map();     // externalTyreId -> reason code (from last rejected add)
  let blockedMap = new Map();   // externalTyreId -> reason ('other-batch'); can't be picked
  let filter = '';

  const root = el(`<div class="tm-rb-picker-root">${pickerHTML({ reference: batch.reference })}</div>`);
  viewEl.replaceChildren(root);

  const rowsEl = root.querySelector('[data-pick-rows]');
  const msgEl = root.querySelector('[data-pick-msg]');
  const countEl = root.querySelector('[data-pick-count]');
  const addBtn = root.querySelector('[data-pick-add]');
  const cancelBtn = root.querySelector('[data-pick-cancel]');
  const searchEl = root.querySelector('[data-pick-search]');

  function setMsg(text, kind) {
    if (!msgEl) return;
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    msgEl.className = `tm-form-msg${kind ? ` tm-form-msg-${kind}` : ''}`;
  }
  function updateFooter() {
    if (countEl) countEl.textContent = `${selected.size} selected`;
    if (addBtn) addBtn.disabled = selected.size === 0;
  }
  function setBusy(on) {
    if (addBtn) addBtn.disabled = on || selected.size === 0;
    if (cancelBtn) cancelBtn.disabled = on;
    if (searchEl) searchEl.disabled = on;
    root.querySelectorAll('[data-pick-check]').forEach((c) => { c.disabled = on; });
  }

  function matches(t) {
    if (!filter) return true;
    const hay = `${t?.externalTyreId ?? ''} ${t?.tyreBrand ?? ''} ${t?.tyreMake ?? ''} ${t?.tyreSize ?? ''}`.toLowerCase();
    return hay.includes(filter);
  }

  function renderRows() {
    if (!candidates.length) { rowsEl.innerHTML = pickerStateRowHTML('No in-stock tyres available to add.'); return; }
    const list = candidates.filter(matches);
    if (!list.length) { rowsEl.innerHTML = pickerStateRowHTML('No tyres match your filter.'); return; }
    rowsEl.innerHTML = list.map((t) => {
      const id = idStr(t.externalTyreId);
      const blocked = blockedMap.has(id);
      return pickerRowHTML(t, {
        checked: selected.has(id),
        disabled: blocked,
        noteText: blocked ? 'On another draft batch' : '',
        errorText: errors.has(id) ? tyreErrorText(errors.get(id)) : ''
      });
    }).join('');
  }

  // Tyres sitting on OTHER draft batches are still In Stock, so they'd show here
  // and the backend would reject them (TYRE_ON_OTHER_BATCH). Find them up front so
  // they're greyed out instead. Best-effort: any failure just leaves them enabled
  // and the all-or-nothing add still guards correctness. (A backend field on the
  // tyre — or an "available for retread" filter — would replace this N+1 sweep with
  // one cheap call.)
  async function loadBlockedSet() {
    try {
      const listRes = await fetchRetreadBatches({ status: RETREAD_BATCH_STATUS.DRAFT });
      if (!listRes.success) return new Map();
      const drafts = (Array.isArray(listRes.batches) ? listRes.batches : [])
        .filter((b) => idStr(b?.retreadBatchId) && idStr(b.retreadBatchId) !== idStr(batchId));
      if (!drafts.length) return new Map();
      const details = await Promise.all(
        drafts.map((b) => fetchRetreadBatch(b.retreadBatchId).catch(() => null))
      );
      const m = new Map();
      for (const d of details) {
        if (!d || !d.success) continue;
        const lines = d.lines || d.batch?.lines || [];
        for (const l of lines) { const id = idStr(l?.externalTyreId ?? l?.tyreId); if (id) m.set(id, 'other-batch'); }
      }
      return m;
    } catch { return new Map(); }
  }

  async function loadCandidates() {
    rowsEl.innerHTML = pickerStateRowHTML('Loading in-stock tyres…');
    const [tyresRes, blocked] = await Promise.all([
      fetchAllTyres({ yardId: getSelectedYardId() }),
      loadBlockedSet()
    ]);
    if (disposed) return;
    blockedMap = blocked;
    if (!tyresRes.success) { rowsEl.innerHTML = pickerStateRowHTML(tyresRes.message || 'Could not load tyres.'); return; }

    const all = Array.isArray(tyresRes.data?.results) ? tyresRes.data.results : [];
    candidates = all
      .filter((t) => effectiveStatus(t) === TYRE_STATUS.IN_STOCK)
      .filter((t) => !already.has(idStr(t.externalTyreId)))
      .sort((a, b) => idStr(a.externalTyreId).localeCompare(idStr(b.externalTyreId)));
    // A blocked tyre can never be selected.
    for (const id of blockedMap.keys()) selected.delete(id);
    renderRows();
    updateFooter();
  }

  async function add() {
    if (!selected.size) return;
    const ids = [...selected];
    setBusy(true);
    setMsg('Adding tyres…');

    const res = await addRetreadBatchLines(batchId, ids, { reason: 'add-lines' });
    if (disposed) return;
    setBusy(false);

    if (!res.success) {
      // Map per-tyre reasons (with the flat id list as a fallback) onto the rows.
      errors.clear();
      const te = Array.isArray(res.tyreErrors) ? res.tyreErrors : [];
      for (const e of te) { const id = idStr(e?.externalTyreId); if (id) errors.set(id, e?.reason); }
      if (!te.length && Array.isArray(res.invalidTyreIds)) {
        for (const id of res.invalidTyreIds) { const s = idStr(id); if (s) errors.set(s, undefined); }
      }
      if (res.status === 403) setMsg('You need administrator access to add tyres.', 'error');
      else if (res.locked) setMsg('This batch is busy with another operation. Try again in a moment.', 'error');
      else if (errors.size) setMsg('Some tyres can’t be added — see the marked rows, deselect them and try again.', 'error');
      else setMsg(res.message || 'Could not add the tyres.', 'error');
      renderRows();
      return;
    }

    // Success. The added tyres are now on the batch (the detail behind has already
    // refetched off the event); drop them from the candidate list and report.
    errors.clear();
    const addedSet = new Set(ids.map(idStr));
    candidates = candidates.filter((t) => !addedSet.has(idStr(t.externalTyreId)));
    selected.clear();
    updateFooter();
    renderRows();

    const capped = (Array.isArray(res.warnings) ? res.warnings : [])
      .filter((w) => w?.code === RETREAD_WARNING.RETREAD_CAP_REACHED);
    const n = ids.length;
    const noun = n === 1 ? 'tyre' : 'tyres';
    if (capped.length) {
      const list = capped.map((w) => w.externalTyreId).filter(Boolean).join(', ');
      setMsg(`Added ${n} ${noun}. At or past the retread limit${list ? `: ${list}` : ''} — check the casings before dispatch.`, 'warn');
    } else {
      setMsg(`Added ${n} ${noun}. Pick more, or go back to the batch.`, 'ok');
    }
  }

  // Delegated + direct wiring.
  root.addEventListener('change', (e) => {
    const cb = e.target.closest('[data-pick-check]');
    if (!cb) return;
    const id = idStr(cb.value);
    if (cb.checked) selected.add(id); else selected.delete(id);
    updateFooter();
  });
  searchEl?.addEventListener('input', () => { filter = searchEl.value.trim().toLowerCase(); renderRows(); });
  addBtn?.addEventListener('click', add);
  cancelBtn?.addEventListener('click', () => { if (typeof ctx.onClose === 'function') ctx.onClose(); });

  loadCandidates();

  return () => { disposed = true; };
}
